import { useCallback, useEffect } from 'react'
import {
  componentNames,
  NAMESPACES,
  OCR_CONFIDENCE_THRESHOLD,
  PHONE_REGEX,
} from '../../../utils/constants'
import {
  Checkbox,
  ControlledMultiSelect,
  EMAIL_REGEX,
  FIELD_VALUE_CONSTANTS,
  FormDateInput,
  FormInput,
  PhoneInput,
  SelectDropDown,
  SvgInfo,
  Tooltip,
  ZipInput,
} from '@ui-kit'
import { useFormContext } from 'react-hook-form'
import { clearFieldApiValidation, DropdownType } from '@app-store'
import CategoryInput from '../../common/CategoryInput'
import { useAppDispatch, useAppSelector } from '../../../utils/agent.store'
import { isEmpty } from 'lodash'
import {
  ConfidenceTooltipProps,
  FieldWrapperProps,
  OCRTemplateElementRendererProps,
} from '../../../types/ocr.types'
import { setupDateRange } from '../../../utils/dateRestrictions'

const ConfidenceTooltip: React.FC<ConfidenceTooltipProps> = ({
  elementKey,
  hasError,
  ocrConfidenceThreshold = OCR_CONFIDENCE_THRESHOLD,
}) => {
  const fieldMetaInfo = useAppSelector(
    (state) => state?.ocrInfoSlice?.fieldMetaState
  )
  if (
    (fieldMetaInfo?.[elementKey]?.confidence ?? 100) >= ocrConfidenceThreshold
  ) {
    return null
  }
  return (
    <span className="text-xs text-yellow-500 leading-none">
      <Tooltip
        content={`Confidence Score: ${fieldMetaInfo?.[
          elementKey
        ]?.confidence?.toFixed(2)}`}
      >
        <SvgInfo
          className={`${
            hasError ? 'svg-path-fill-error-500' : 'svg-path-fill-progress-500'
          } cursor-pointer`}
        />
      </Tooltip>
    </span>
  )
}

const FieldWrapper: React.FC<FieldWrapperProps> = ({
  elementKey,
  children,
  ocrConfidenceThreshold = OCR_CONFIDENCE_THRESHOLD,
}) => {
  const dispatch = useAppDispatch()
  const { formState } = useFormContext()
  const error = formState?.errors?.[elementKey]
  const isDirty = !!formState?.dirtyFields?.[elementKey]
  const fieldMetaInfo = useAppSelector(
    (state) => state?.ocrInfoSlice?.fieldMetaState
  )

  const fieldMeta = fieldMetaInfo?.[elementKey]
  const apiValidationMessage = fieldMeta?.validation_message

  const clearApiValidationIfNeeded = useCallback(() => {
    const meta = fieldMetaInfo?.[elementKey]
    if (!meta) return
    if (meta.is_valid !== false && !meta.validation_message) return
    dispatch(clearFieldApiValidation(elementKey))
  }, [dispatch, elementKey, fieldMetaInfo])

  useEffect(() => {
    if (!isDirty) return
    clearApiValidationIfNeeded()
  }, [clearApiValidationIfNeeded, isDirty])

  const showConfidence =
    (fieldMetaInfo?.[elementKey]?.confidence ?? 100) < ocrConfidenceThreshold
  const displayError = apiValidationMessage || error
  const showMetaRow = !!(apiValidationMessage || error) || showConfidence

  return (
    <div onFocus={clearApiValidationIfNeeded}>
      {children}
      {showMetaRow && (
        <div className="grid grid-cols-2 mt-1">
          <div>
            {!!(apiValidationMessage || error) && (
              <span className="text-xs font-primary font-light text-error-500">
                {apiValidationMessage ||
                  (isEmpty(error?.message?.toString())
                    ? NAMESPACES?.MANDATORY_FIELD
                    : error?.message?.toString())}
              </span>
            )}
          </div>
          <div className="justify-self-end">
            <ConfidenceTooltip
              elementKey={elementKey}
              hasError={!!displayError}
              ocrConfidenceThreshold={ocrConfidenceThreshold}
            />
          </div>
        </div>
      )}
    </div>
  )
}

const OCRTemplateElementRenderer: React.FC<OCRTemplateElementRendererProps> = ({
  element,
  elementMeta,
  ocrConfidenceThreshold = OCR_CONFIDENCE_THRESHOLD,
}) => {
  const { register, setValue, control, formState } = useFormContext()
  const fieldMetaInfo = useAppSelector(
    (state) => state?.ocrInfoSlice?.fieldMetaState
  )

  const evaluateFieldBorderClass = (
    key: string,
    ui_data_type: (typeof componentNames)[keyof typeof componentNames]
  ) => {
    const isInvalid = fieldMetaInfo?.[key]?.is_valid === false

    switch (ui_data_type) {
      case componentNames.SINGLE_LINE:
      case componentNames.EMAIL:
      case componentNames.PHONE_NUMBER:
      case componentNames.ZIP_CODE:
      case componentNames.DATE:
      case componentNames.NUMBER:
        if (formState?.errors && formState?.errors[key]) {
          return '!border-error-500'
        } else if (isInvalid) {
          return '!border-error-500'
        } else if (
          fieldMetaInfo &&
          fieldMetaInfo[key]?.confidence < ocrConfidenceThreshold
        ) {
          return '!border-progress-500'
        } else {
          return '!group-focus-within:border-secondary-500'
        }
      case componentNames.CATEGORY:
      case componentNames.MULTI_SELECT_DROPDOWN:
      case componentNames.DROPDOWN:
        if (formState?.errors && formState?.errors[key]) {
          return '!border-error-500'
        } else if (isInvalid) {
          return '!border-error-500'
        } else if (
          fieldMetaInfo &&
          fieldMetaInfo[key]?.confidence < ocrConfidenceThreshold
        ) {
          return '!border-progress-500'
        }
        break
      default:
        return ''
    }
  }

  switch (elementMeta?.ui_data_type) {
    case componentNames?.SINGLE_LINE:
      return (
        <FieldWrapper
          elementKey={element?.key}
          ocrConfidenceThreshold={ocrConfidenceThreshold}
        >
          <FormInput
            name={element?.key}
            key={element?.key}
            label={elementMeta?.label}
            validation={{
              required: elementMeta?.required ?? false,
              ...(elementMeta?.custom_regex
                ? {
                    pattern: {
                      value: new RegExp(elementMeta.custom_regex),
                      message: NAMESPACES.INVALID_MSG,
                    },
                  }
                : {}),
              ...(elementMeta?.char_limit
                ? {
                    maxLength: {
                      value: Number(elementMeta.char_limit),
                      message: `Maximum ${elementMeta.char_limit} characters are allowed`,
                    },
                  }
                : {}),
            }}
            register={register}
            maxLength={
              elementMeta?.char_limit
                ? Number(elementMeta.char_limit)
                : undefined
            }
            inputBorderClass={`border border-solid group-focus-within:border ${evaluateFieldBorderClass(
              element?.key,
              elementMeta?.ui_data_type
            )}`}
          />
        </FieldWrapper>
      )
    case componentNames?.NUMBER:
      return (
        <FieldWrapper
          elementKey={element?.key}
          ocrConfidenceThreshold={ocrConfidenceThreshold}
        >
          <FormInput
            name={element?.key}
            key={element?.key}
            label={elementMeta?.label}
            // Use text (not type=number) so digit IDs like NPI keep leading zeros
            // and RHF custom_regex / maxLength validate against the real string.
            type="text"
            inputMode="numeric"
            onKeyDown={(evt) =>
              ['e', 'E', '+', '-', '.'].includes(evt.key) &&
              evt.preventDefault()
            }
            validation={{
              required: elementMeta?.required ?? false,
              ...(elementMeta?.custom_regex
                ? {
                    validate: {
                      customRegex: (value: unknown) => {
                        if (
                          value === '' ||
                          value === null ||
                          value === undefined
                        ) {
                          return true
                        }
                        const regex = new RegExp(
                          elementMeta.custom_regex as string
                        )
                        return (
                          regex.test(String(value)) || NAMESPACES.INVALID_MSG
                        )
                      },
                    },
                  }
                : {}),
              ...(elementMeta?.char_limit
                ? {
                    maxLength: {
                      value: Number(elementMeta.char_limit),
                      message: `Maximum ${elementMeta.char_limit} characters are allowed`,
                    },
                  }
                : {}),
            }}
            register={register}
            maxLength={
              elementMeta?.char_limit
                ? Number(elementMeta.char_limit)
                : undefined
            }
            onInput={
              elementMeta?.char_limit
                ? (e: React.FormEvent<HTMLInputElement>) => {
                    const input = e.currentTarget
                    if (input.value.length > Number(elementMeta.char_limit)) {
                      input.value = input.value.slice(
                        0,
                        Number(elementMeta.char_limit)
                      )
                    }
                  }
                : undefined
            }
            inputBorderClass={`border border-solid group-focus-within:border ${evaluateFieldBorderClass(
              element?.key,
              elementMeta?.ui_data_type
            )}`}
          />
        </FieldWrapper>
      )
    case componentNames?.EMAIL:
      return (
        <FieldWrapper
          elementKey={element?.key}
          ocrConfidenceThreshold={ocrConfidenceThreshold}
        >
          <FormInput
            name={element?.key}
            key={element?.key}
            label={elementMeta?.label}
            validation={{
              required: elementMeta?.required ?? false,
              pattern: {
                value: EMAIL_REGEX,
                message: NAMESPACES.EMAIL_VALIDATION_MSG,
              },
            }}
            register={register}
            inputBorderClass={`border border-solid group-focus-within:border ${evaluateFieldBorderClass(
              element?.key,
              elementMeta?.ui_data_type
            )}`}
          />
        </FieldWrapper>
      )
    case componentNames?.PHONE_NUMBER:
      return (
        <FieldWrapper
          elementKey={element?.key}
          ocrConfidenceThreshold={ocrConfidenceThreshold}
        >
          <PhoneInput
            register={register}
            key={element?.key}
            name={element.key}
            validation={{
              required: elementMeta?.required ?? false,
              pattern: {
                value: PHONE_REGEX,
                message: NAMESPACES.PHONE_VALIDATION_MSG,
              },
            }}
            label={elementMeta.label}
            setValue={setValue}
            // control={control}
            dataQaId={element.key ?? ''}
            inputBorderClass={`border border-solid group-focus-within:border ${evaluateFieldBorderClass(
              element?.key,
              elementMeta?.ui_data_type
            )}`}
          />
        </FieldWrapper>
      )
    case componentNames?.DATE: {
      const dateRange = setupDateRange({ meta: elementMeta })
      return (
        <FieldWrapper
          elementKey={element?.key}
          ocrConfidenceThreshold={ocrConfidenceThreshold}
        >
          <FormDateInput
            control={control}
            name={element?.key}
            key={element?.key}
            min={dateRange.minDate}
            max={dateRange.maxDate}
            label={elementMeta?.label}
            required={elementMeta?.required}
            showErrors={false}
            handleChange={(date) => {
              setValue(element.key, date, { shouldDirty: true })
            }}
            borderStyles={evaluateFieldBorderClass(
              element?.key,
              elementMeta?.ui_data_type
            )}
          />
        </FieldWrapper>
      )
    }
    case componentNames?.CATEGORY:
      return (
        <FieldWrapper
          elementKey={element?.key}
          ocrConfidenceThreshold={ocrConfidenceThreshold}
        >
          <CategoryInput
            name={element.key}
            label={elementMeta?.label}
            key={element?.key}
            control={control}
            isSearchable={true}
            onChange={(e: DropdownType<string>) => {
              setValue(element.key, e?.value, { shouldDirty: true })
            }}
            setValue={setValue}
            required={elementMeta?.required}
            categoryKey={elementMeta?.category_key ?? ''}
            searchLength={FIELD_VALUE_CONSTANTS?.TWO}
            dataQaId={element.key ?? ''}
            borderClassName={`border border-solid group-focus-within:border ${evaluateFieldBorderClass(
              element?.key,
              elementMeta?.ui_data_type
            )}`}
          />
        </FieldWrapper>
      )
    case componentNames?.ZIP_CODE:
      return (
        <FieldWrapper
          elementKey={element?.key}
          ocrConfidenceThreshold={ocrConfidenceThreshold}
        >
          <ZipInput
            register={register}
            name={element.key}
            // errors={formState?.errors}
            key={element?.key}
            label={elementMeta?.label}
            setValue={setValue}
            control={control}
            dataQaId={element.key ?? ''}
            inputBorderClass={`border border-solid group-focus-within:border ${evaluateFieldBorderClass(
              element?.key,
              elementMeta?.ui_data_type
            )}`}
            validation={{
              required: elementMeta?.required ?? false,
            }}
          />
        </FieldWrapper>
      )

    case componentNames?.DROPDOWN:
      return (
        <FieldWrapper
          elementKey={element?.key}
          ocrConfidenceThreshold={ocrConfidenceThreshold}
        >
          <SelectDropDown
            name={element.key}
            label={elementMeta?.label}
            key={element?.key}
            options={(elementMeta.control_values as unknown as string[])?.map(
              (val) => ({
                label: val,
                value: val,
              })
            )}
            onChange={(e: DropdownType<string>) => {
              setValue(element.key, e?.value, { shouldDirty: true })
            }}
            required={elementMeta?.required}
            className={evaluateFieldBorderClass(
              element?.key,
              elementMeta?.ui_data_type
            )}
          />
        </FieldWrapper>
      )
    case componentNames?.MULTI_SELECT_DROPDOWN:
      return (
        <FieldWrapper
          elementKey={element?.key}
          ocrConfidenceThreshold={ocrConfidenceThreshold}
        >
          <ControlledMultiSelect
            control={control}
            name={element.key}
            label={elementMeta?.label}
            key={element?.key}
            options={(elementMeta.control_values as unknown as string[])?.map(
              (val) => ({
                label: val,
                value: val,
              })
            )}
            onChange={(e: DropdownType<string>[]) => {
              const valuesArray = e.map((item) => item.value)
              setValue(element.key, valuesArray, { shouldDirty: true })
            }}
            required={elementMeta?.required}
            borderStyles={evaluateFieldBorderClass(
              element?.key,
              elementMeta?.ui_data_type
            )}
          />
        </FieldWrapper>
      )
    case componentNames.CONSENTS:
      return (
        <div>
          <div className="flex items-center gap-1">
            <Checkbox
              label={elementMeta?.label}
              name={element.key}
              register={register}
            />
            <ConfidenceTooltip
              elementKey={element?.key}
              hasError={!!formState?.errors?.[element?.key]}
            />
          </div>
          {!isEmpty(formState?.errors?.[element?.key]) && (
            <span className="text-xs font-primary font-light text-error-500 mt-1">
              {isEmpty(formState?.errors?.[element?.key]?.message?.toString())
                ? NAMESPACES?.MANDATORY_FIELD
                : formState?.errors?.[element?.key]?.message?.toString()}
            </span>
          )}
        </div>
      )
    default:
      return <span>{element?.meta?.ui_data_type}</span>
  }
}

export default OCRTemplateElementRenderer
